import type { AxiosRequestConfig } from 'axios';
import apiClient from '@/api/client';
import type { LoginResponse } from '@/api/endpoints/auth';

// ── Types ──────────────────────────────────────────────

export interface SignupRequest {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  company_name: string;
  country_code: string;
  industry?: string;
  phone?: string;
  /** 6-digit OTP code verified server-side inside signup — makes OTP check
   * atomic with account creation so a signup failure cannot leave the user
   * with a consumed/expired OTP and no way to retry. */
  otp_code?: string;
  // Team context
  company_size?: string;
  // Marketing → signup intent + attribution (all optional)
  plan_slug?: string;
  /** Billing cadence picked on the marketing site (annual / monthly / quarterly).
   * Optional — falls through to the plan's default when omitted. */
  billing_interval?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landing_path?: string;
  partner_ref?: string;
}

/**
 * The signup endpoint now atomically creates the account AND logs the user in —
 * httpOnly session cookies are set on the response and the body mirrors the
 * login payload so callers can route straight to the resolved homepage without
 * a second `/auth/login` round-trip.
 */
export type SignupResponse = LoginResponse;

export interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  base_price_monthly: number;
  base_price_annual: number;
  included_users: number;
  per_user_price: number;
  currency: string;
  max_companies: number;
  max_employees: number;
  enabled_modules: string[];
  trial_days: number;
  is_active: boolean;
  sort_order: number;
}

export interface PlanCreate {
  name: string;
  slug: string;
  description?: string;
  base_price_monthly: number;
  base_price_annual: number;
  included_users?: number;
  per_user_price?: number;
  currency?: string;
  max_companies?: number;
  max_employees?: number;
  enabled_modules?: string[];
  trial_days?: number;
  is_active?: boolean;
  sort_order?: number;
}

export interface PlanUpdate {
  name?: string;
  description?: string;
  base_price_monthly?: number;
  base_price_annual?: number;
  included_users?: number;
  per_user_price?: number;
  max_companies?: number;
  max_employees?: number;
  enabled_modules?: string[];
  trial_days?: number;
  is_active?: boolean;
  sort_order?: number;
}

export interface Subscription {
  id: string;
  group_id: string;
  plan_id: string;
  status: string;
  billing_interval: string;
  current_period_start: string | null;
  current_period_end: string | null;
  trial_ends_at: string | null;
  canceled_at: string | null;
  cancel_reason: string | null;
  stripe_customer_id: string | null;
  currency: string;
}

export interface Usage {
  active_users: number;
  active_companies: number;
  active_employees: number;
  included_users: number;
  max_companies: number;
  max_employees: number;
  extra_users: number;
  extra_user_cost: number;
}

export interface SubscriptionWithUsage extends Subscription {
  plan_name: string;
  plan_slug: string;
  plan_price_monthly: number;
  plan_price_annual: number;
  /** Populated when the subscription is managed via Stripe. Null/absent for
   *  Paystack / Flutterwave subscriptions. Used to gate the pause/resume UI. */
  stripe_subscription_id?: string | null;
  /** BILL-23: usage arrives as FLAT fields on the wire (see backend
   * SubscriptionWithUsage schema) — the old nested `usage` object never
   * existed in any response, so the usage panel silently never rendered. */
  active_users: number;
  active_companies: number;
  active_employees: number;
  included_users: number;
  max_companies: number;
  max_employees: number;
  per_user_price: number;
  /** Non-PII card descriptor captured from gateway webhook (PCI SAQ-A — PAN never stored). */
  card_brand?: string | null;
  card_last4?: string | null;
  card_exp_month?: number | null;
  card_exp_year?: number | null;
  /** P3-F5 price-lock. Founding cohort = price locked forever (no migration).
   *  Standard cohort: `price_locked_until` is the ISO date the current pricing
   *  holds until, after which the account moves to the latest published price. */
  is_founding_cohort?: boolean;
  price_locked_until?: string | null;
}

/** One row in the credits ledger. Positive = credit given, negative = consumed. */
export interface AccountCreditEntry {
  id: string;
  amount: number;
  currency: string;
  kind: string;
  description: string | null;
  expires_at: string | null;
  created_at: string;
}

/** Full credit balance + ledger for the calling group. */
export interface CreditsLedgerResponse {
  balance: number;
  currency: string;
  entries: AccountCreditEntry[];
}

export interface BillingInvoice {
  id: string;
  subscription_id: string;
  amount: number;
  currency: string;
  status: string;
  period_start: string | null;
  period_end: string | null;
  pdf_url: string | null;
  description: string | null;
  created_at: string;
  // NRS / FIRS e-invoicing fields (enriched by backend)
  invoice_number: string | null;
  invoice_date: string | null;
  supplier_name: string | null;
  supplier_tin: string | null;
  customer_tin: string | null;
  taxable_amount_cents: number | null;
  vat_amount_cents: number | null;
  vat_rate: number | null;
  total_amount_cents: number | null;
}

export interface AvailableGateway {
  key: string;
  label: string;
  description: string;
  supported_currencies: string[];
  is_default: boolean;
  supports_recurring: boolean;
  recurring_unsupported_reason?: string | null;
}

export interface AvailableGatewaysResponse {
  country_code: string | null;
  gateways: AvailableGateway[];
  default: string;
}

export interface PlatformGatewayItem {
  key: string;
  label: string;
  description: string;
  is_configured: boolean;
  is_default: boolean;
}

export interface PlatformGatewaysResponse {
  default: string | null;
  source: 'db' | 'env' | 'auto';
  items: PlatformGatewayItem[];
}

export interface TenantGatewayItem {
  gateway: string;
  configured: boolean;
  config: Record<string, unknown> | null;
}

export interface TenantGatewayConfigRequest {
  secret_key: string;
  public_key?: string;
  webhook_secret?: string;
  encryption_key?: string;
  test_mode?: boolean;
}

export interface UsageWarning {
  resource: 'employees' | 'companies' | 'users';
  current: number;
  limit: number;
  percent: number;
  severity: 'warning' | 'critical' | 'exceeded';
  message: string;
  plan_name: string | null;
}

export interface UsageWarningsResponse {
  warnings: UsageWarning[];
}

export interface PlanChangePreview {
  current_plan_slug: string | null;
  current_plan_name: string | null;
  target_plan_slug: string;
  target_plan_name: string;
  is_upgrade: boolean;
  is_downgrade: boolean;
  current_price_monthly: number;
  target_price_monthly: number;
  price_delta_monthly: number;
  /** ISO currency code for the amounts above (e.g. KES, NGN). Optional until
   *  the tenant's localized pricing is resolved server-side. */
  currency?: string;
  modules_gained: string[];
  modules_lost: string[];
  employee_limit_delta: number;
  company_limit_delta: number;
  usage_blockers: string[];
  billing_interval: string;
}

// ── Interval change (monthly ↔ annual) ─────────────────
// preview=true returns this proration estimate; the amounts are display-only —
// the gateway applies exact proration on the next payment.
export interface ChangeIntervalPreview {
  current_interval: string;
  new_interval: string;
  current_period_end: string | null;
  proration_credit: string | null;
  new_amount: string | null;
  currency: string;
  note: string;
}

// preview=false returns a gateway checkout session (when a price reference
// exists) so the gateway can re-sync the new interval.
export interface CheckoutResponse {
  checkout_url: string;
  session_id: string;
  gateway: string;
}

// ── Add-on entitlement types ───────────────────────────
// Returned by GET /app/subscription/addons. Mirrors companies_products rows
// so the billing page can show which add-ons (e.g. SEO Engine) are active.
export interface SubscriptionAddon {
  product_slug: string;
  status: string; // 'active' | 'inactive' | 'trialing' | ...
  is_active: boolean; // derived: live membership (active/trial/trialing/past_due)
  tier: string | null; // e.g. 'starter'
  monthly_cost: number | null; // launch price (backend SOT) — render this, don't hardcode
  currency: string | null; // e.g. 'USD'
  upgrade_url: string | null;
}

export interface SubscriptionAddonsResponse {
  addons: SubscriptionAddon[];
}

// PMB-F4 — à-la-carte add-ons the tenant can buy (GET /app/addons/available).
export interface AvailableAddon {
  module_key: string;
  name: string;
  monthly_price: number | null;
  annual_price: number | null;
  currency: string;
  already_active: boolean;
}

export interface AvailableAddonsResponse {
  addons: AvailableAddon[];
}

// Returned by GET /app/subscription/seo-usage (SEO-4-D). Current-month SEO
// Engine AI usage for the caller's company, aggregated from ai_credit_ledger
// (marketing_seo_generate + blog_draft_batch). Words = generate calls,
// drafts = blog-batch runs, cost in minor units of `currency`.
export interface SeoUsageResponse {
  seo_generations: number;
  drafts_run: number;
  credits_used: number;
  cost_this_month_cents: number;
  currency: string;
  period_start: string | null;
}

// ── Country pricing override types ─────────────────────
// Structured payload mirrors profiles/<cc>/pricing/plans.json so the admin
// form can render the full schema; any subset is a valid override.
export interface CountryPricingOverrideRow {
  id: string;
  country_code: string;
  overrides: Record<string, unknown>;
  notes: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface EffectiveCountryPricing {
  country_code: string;
  effective: Record<string, any>;
  baseline: Record<string, any>;
  has_override: boolean;
  signoff: {
    status?: string;
    owner_name?: string | null;
    owner_role?: string;
    signed_off_at?: string | null;
    effective_from?: string | null;
    blockers?: string[];
    notes?: string;
  };
  signoff_approved: boolean;
}

// BIL-03 — Coupon validate response
export interface CouponValidateResponse {
  valid: boolean;
  discount_type?: 'percent' | 'fixed';
  discount_value?: number;
  currency?: string | null;
  /** Human-readable label e.g. "20% off" or "NGN 5,000 off" */
  display_label?: string;
  error?: 'expired' | 'exhausted' | 'invalid' | 'wrong_plan' | 'not_started' | string;
}

/** Token bundle for rendering an inline card/bank widget (PCI SAQ-A). */
export interface InlinePaymentMethodInit {
  provider: string;
  mode: 'inline' | 'redirect';
  public_key: string;
  access_code: string | null;
  client_secret: string | null;
  reference: string | null;
  email: string;
  channels: string[];
  redirect_url: string | null;
}

/** Itemised, non-committing checkout breakdown (Hostinger-style). */
export interface CheckoutQuote {
  currency: string;
  interval: string;
  plan_name: string;
  subtotal: string;
  discount: string;
  discount_label: string | null;
  coupon_valid: boolean;
  coupon_error: string | null;
  tax: string;
  tax_label: string | null;
  credit: string;
  total: string;
  annual_savings_pct: number | null;
  renews_on: string | null;
}

// ── API ────────────────────────────────────────────────

export const billingApi = {
  // Public
  signup: (data: SignupRequest) =>
    apiClient.post<SignupResponse>('/signup', data, {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }),

  listPlans: (countryCode?: string) =>
    apiClient.get<Plan[]>('/plans', {
      params: countryCode ? { country_code: countryCode } : undefined,
    }),

  listAvailableGateways: (countryCode?: string) =>
    apiClient.get<AvailableGatewaysResponse>('/billing/available-gateways', {
      params: countryCode ? { country_code: countryCode } : undefined,
    }),

  // Authenticated
  getSubscription: () =>
    apiClient.get<SubscriptionWithUsage>('/billing/subscription'),
  /** For the shell banners every role renders: a role without billing access gets a 403 here,
   *  which is a normal "no banner" answer, not an error to toast on every page. */
  getSubscriptionForBanner: () =>
    apiClient.get<SubscriptionWithUsage>('/billing/subscription', { _silentOnError: true } as AxiosRequestConfig),

  getUpgradePitch: (payload: {
    resource: string;
    current: number;
    limit: number;
    plan: string;
  }) =>
    apiClient.post<{ pitch: string; is_ai: boolean }>(
      '/billing/upgrade-pitch',
      payload,
    ),

  getUsage: () =>
    apiClient.get<Usage>('/billing/usage'),

  getUsageWarnings: () =>
    apiClient.get<UsageWarningsResponse>('/billing/usage-warnings'),

  getInvoices: () =>
    apiClient.get<BillingInvoice[]>('/billing/invoices'),

  previewPlanChange: (targetSlug: string) =>
    apiClient.get<PlanChangePreview>(`/billing/plans/${targetSlug}/preview`),

  // Switch billing cadence (monthly ↔ annual) for the current subscription.
  // preview:true returns a ChangeIntervalPreview (proration estimate, no change);
  // preview:false executes — returning a gateway checkout session (with
  // checkout_url) when a price reference exists, or a {updated} acknowledgement
  // when the interval is updated in-DB only.
  changeInterval: (body: {
    billing_interval: 'annual' | 'monthly';
    preview: boolean;
    success_url?: string;
  }) =>
    apiClient
      .post<
        | ChangeIntervalPreview
        | (Partial<CheckoutResponse> & {
            updated?: boolean;
            billing_interval?: string;
            checkout_url?: string | null;
          })
      >('/billing/subscription/change-interval', body, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      .then((r) => r.data),

  submitCancelFeedback: (reason_code: string, feedback_text?: string) =>
    apiClient.post<{ ok: boolean }>('/billing/cancel-feedback', {
      reason_code,
      feedback_text: feedback_text || null,
    }),

  cancelSubscription: (
    effective: 'immediate' | 'end_of_period',
    reason?: string,
  ) =>
    apiClient
      .post<SubscriptionWithUsage>('/billing/subscription/cancel', { effective, reason })
      .then((r) => r.data),

  createCheckout: (
    planSlug: string,
    interval: 'monthly' | 'annual',
    gateway?: string,
    couponCode?: string,
  ) =>
    apiClient.post<{ checkout_url: string; session_id: string; gateway: string }>(
      '/billing/checkout',
      {
        plan_slug: planSlug,
        billing_interval: interval,
        ...(gateway ? { gateway } : {}),
        ...(couponCode ? { coupon_code: couponCode } : {}),
      },
      { headers: { 'Idempotency-Key': crypto.randomUUID() } },
    ),

  // BIL-03: Validate a promo code without consuming it.
  validateCoupon: (body: { code: string; plan_slug: string; country_code?: string }) =>
    apiClient.post<CouponValidateResponse>('/billing/coupons/validate', body),

  openPortal: () =>
    apiClient.post<{ portal_url: string }>('/billing/portal', {}),

  getCardUpdateUrl: () =>
    apiClient.get<{ url: string | null; expires_in_seconds: number }>(
      '/billing/card-update-url',
    ),

  /** Itemised, non-committing checkout breakdown for the Subscription page. */
  getCheckoutQuote: (body: {
    plan_slug: string;
    billing_interval?: string;
    coupon_code?: string;
  }) => apiClient.post<CheckoutQuote>('/billing/checkout/quote', body),

  /** Initialize an inline card/bank capture (Paystack Inline / Stripe). */
  initInlinePaymentMethod: () =>
    apiClient.post<InlinePaymentMethodInit>('/billing/payment-method/inline-init', {}),

  /** Current account credit balance + ledger for the calling group. */
  getCredits: () =>
    apiClient.get<CreditsLedgerResponse>('/billing/credits'),

  // Add-on entitlements (SEO Engine, etc.). Falls back to the subscription
  // payload if the dedicated endpoint isn't available in this environment.
  getAddons: () =>
    apiClient.get<SubscriptionAddonsResponse>('/app/subscription/addons'),

  // PMB-F4 — purchasable à-la-carte add-ons + recurring checkout.
  getAvailableAddons: () =>
    apiClient.get<AvailableAddonsResponse>('/app/addons/available'),

  checkoutAddon: (moduleKey: string, interval: 'monthly' | 'annual') =>
    apiClient.post<{ checkout_url: string; session_id: string; gateway: string }>(
      `/billing/addons/${moduleKey}/checkout`,
      { billing_interval: interval },
    ),

  // SEO-4-D — current-month SEO Engine AI usage for the caller's company.
  getSeoUsage: () =>
    apiClient.get<SeoUsageResponse>('/app/subscription/seo-usage'),

  // Country pricing overrides — admin overlay on profiles/<cc>/pricing/plans.json
  listCountryPricingOverrides: () =>
    apiClient.get<CountryPricingOverrideRow[]>('/platform/pricing'),

  getEffectiveCountryPricing: (countryCode: string) =>
    apiClient.get<EffectiveCountryPricing>(
      `/platform/pricing/${countryCode.toUpperCase()}`,
    ),

  upsertCountryPricingOverride: (
    countryCode: string,
    overrides: Record<string, unknown>,
    notes?: string,
  ) =>
    apiClient.put<CountryPricingOverrideRow>(
      `/platform/pricing/${countryCode.toUpperCase()}`,
      { overrides, notes: notes ?? null },
    ),

  deleteCountryPricingOverride: (countryCode: string) =>
    apiClient.delete<void>(`/platform/pricing/${countryCode.toUpperCase()}`),

  // Direct plan switch — no gateway checkout. Use when target plan is free or
  // has no gateway price reference. Backend enforces usage blockers.
  switchPlan: (planSlug: string, interval: 'monthly' | 'annual') =>
    apiClient.post<{ switched: boolean; plan_slug: string; plan_name: string; message: string }>(
      '/billing/switch-plan',
      { plan_slug: planSlug, billing_interval: interval },
    ),

  // Plan CRUD — moved to /platform/plans in Sprint 1 (ADR-0001).
  // Backend 410s the old /admin/plans paths; these methods now target the
  // platform console and 403 for any caller without platform_owner role.
  createPlan: (data: PlanCreate) =>
    apiClient.post<Plan>('/platform/plans', data),

  updatePlan: (planId: string, data: PlanUpdate) =>
    apiClient.patch<Plan>(`/platform/plans/${planId}`, data),

  listAllPlans: () =>
    apiClient.get<Plan[]>('/plans'),

  // Platform SaaS billing gateway selector — moved to /platform/billing/gateways
  // in Sprint 1 (ADR-0001). Requires platform_finance role or higher.
  getPlatformGateways: () =>
    apiClient.get<PlatformGatewaysResponse>('/platform/billing/gateways'),

  setPlatformDefaultGateway: (gateway: string) =>
    apiClient.patch<PlatformGatewaysResponse>(
      '/platform/billing/gateways/default',
      { gateway },
    ),

  // Tenant — per-company gateway config (secrets encrypted at rest)
  listCompanyGateways: () =>
    apiClient.get<TenantGatewayItem[]>('/settings/gateways'),

  upsertCompanyGateway: (gatewayKey: string, data: TenantGatewayConfigRequest) =>
    apiClient.put<TenantGatewayItem>(`/settings/gateways/${gatewayKey}`, data),

  deleteCompanyGateway: (gatewayKey: string) =>
    apiClient.delete<void>(`/settings/gateways/${gatewayKey}`),

  // BIL-07 — tenant-side subscription pause / resume (Stripe only)
  pauseSubscription: () =>
    apiClient.post<SubscriptionWithUsage>('/billing/subscription/pause', {}),

  resumeSubscription: () =>
    apiClient.post<SubscriptionWithUsage>('/billing/subscription/resume', {}),

  /** Verify a Paystack redirect reference directly against the gateway.
   * Used to confirm payment without waiting for the webhook. */
  verifyPayment: (reference: string) =>
    apiClient.get<{ status: string; amount: string | null; currency: string; reference: string }>(
      '/billing/verify-payment',
      { params: { reference } },
    ),

  /** Actively recover a checkout when the settlement signal is slow/lost
   * (2026-09-10) -- re-verifies directly with OwoPort and, if settled,
   * applies the same activation logic the webhook/relay would have.
   * Unlike verifyPayment (read-only), this can actually change the
   * subscription -- call it deliberately (a "Check again"/recovery
   * action), not on a passive interval. Safe to call repeatedly. */
  reconcileCheckout: () =>
    apiClient.post<{
      status?: string;
      reason?: string;
      retryable?: boolean;
      reconcile_result?: { status: string; event_type?: string };
      subscription?: SubscriptionWithUsage;
    }>('/billing/checkout/reconcile', {}),
};
